import { liveQuery } from "dexie";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { getGatewayBaseUrl } from "../runtime/config";
import {
  PosShiftCloseGatewayClient,
  type ShiftCloseCommitResult,
  type ShiftCloseCountLine,
  type ShiftCloseDenomination,
  type ShiftCloseRuntime,
} from "../runtime/gateway/shift-close-client";
import { syncPendingCashSales } from "../runtime/local-store/cash-sale-state";
import {
  readLocalEmployeeSession,
  type LocalEmployeeSessionState,
} from "../runtime/local-store/employee-session";
import { countLocalParkedCarts } from "../runtime/local-store/parked-cart-state";
import { posRuntimeDb } from "../runtime/local-store/pos-db";
import { readShiftCloseRuntime, saveShiftCloseRuntime } from "../runtime/local-store/secondary-runtime-cache";
import {
  clearLocalShift,
  readLocalShift,
  saveCoreShift,
  type LocalShiftState,
} from "../runtime/local-store/shift-state";
import {
  readTerminalCredential,
  type TerminalCredentialState,
  type TrustedTerminalIdentity,
} from "../runtime/local-store/terminal-state";
import "./ShiftCloseRuntimeHost.css";

type RuntimeSnapshot = {
  terminal: TerminalCredentialState | null;
  session: LocalEmployeeSessionState | null;
  shift: LocalShiftState | null;
};

type CountDraft = Record<string, { retained: string; pending: string }>;
type Phase = "loading" | "start" | "count" | "review" | "success";

type ConfirmedCount = {
  confirmationEventId: string;
  actualCash: number;
  retainedFloat: number;
  pendingDeposit: number;
};

function randomKey(prefix: string) {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${id}`;
}

function formatMoney(value: number, currencyCode = "THB") {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDateTime(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
}

function errorText(value: string) {
  if (value.includes("GATEWAY_UNREACHABLE")) return "ติดต่อ MORE POS Gateway ไม่ได้ กรุณาตรวจอินเทอร์เน็ต";
  if (value.includes("GATEWAY_TIMEOUT")) return "Gateway ตอบกลับช้าเกินไป กรุณาลองใหม่";
  if (value.includes("PERMISSION_DENIED")) return "พนักงานคนนี้ไม่มีสิทธิ์ปิดกะ";
  if (value.includes("RESPONSIBLE_ACTOR_REQUIRED")) return "ผู้ปิดกะต้องเป็นผู้รับผิดชอบกะนี้";
  if (value.includes("RUNTIME_SESSION_INVALID")) return "เซสชันพนักงานหมดอายุ กรุณา Login ใหม่";
  if (value.includes("ACTIVE_SHIFT_REQUIRED")) return "ไม่พบกะที่กำลังเปิดอยู่บนเครื่องนี้";
  if (value.includes("MUST_BE_OPEN")) return "กะนี้ไม่ได้อยู่ในสถานะพร้อมเริ่มปิดกะ";
  if (value.includes("MUST_BE_CLOSING")) return "กะนี้ไม่ได้อยู่ในขั้นตอนปิดกะแล้ว กรุณาอ่านสถานะใหม่";
  if (value.includes("COUNT_DENOMINATION_SET_INVALID") || value.includes("COUNT_LINE_INVALID")) {
    return "รายการชนิดเงินไม่ตรงกับมาตรฐานปัจจุบัน กรุณาโหลดใหม่";
  }
  if (value.includes("COUNT_CONFIRMATION_INVALID")) {
    return "ผลนับเงินยืนยันไม่ตรงกับรอบปิดกะปัจจุบัน กรุณานับใหม่";
  }
  if (value.includes("CASH_SALE_SYNC_FAILED")) return "ยัง Sync รายการเงินสดไม่ครบ จึงยังเริ่มปิดกะไม่ได้";
  return value || "ทำรายการปิดกะไม่สำเร็จ";
}

function emptyDraft(denominations: ShiftCloseDenomination[]): CountDraft {
  return Object.fromEntries(
    denominations.map((denomination) => [
      denomination.denomination_code,
      { retained: "", pending: "" },
    ]),
  );
}

function localScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

async function countPendingCashImpactOutbox(identity: TrustedTerminalIdentity) {
  const scopeKey = localScope(identity);
  return posRuntimeDb.outbox
    .where("scopeKey")
    .equals(scopeKey)
    .filter(
      (record) =>
        record.operation === "SYNC_COMMERCE_CASH_SALE" &&
        record.state !== "ACKED",
    )
    .count();
}

export function ShiftCloseRuntimeHost() {
  const [snapshot, setSnapshot] = useState<RuntimeSnapshot>({
    terminal: null,
    session: null,
    shift: null,
  });
  const [mountNode, setMountNode] = useState<Element | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [open, setOpen] = useState(false);
  const [runtime, setRuntime] = useState<ShiftCloseRuntime | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [roundNo, setRoundNo] = useState<1 | 2>(1);
  const [draft, setDraft] = useState<CountDraft>({});
  const [attemptKey, setAttemptKey] = useState(() => randomKey("pos-shift-count-attempt"));
  const [roundRequestKey, setRoundRequestKey] = useState(() => randomKey("pos-shift-count-round"));
  const [beginRequestKey, setBeginRequestKey] = useState(() => randomKey("pos-shift-close-begin"));
  const [cancelRequestKey, setCancelRequestKey] = useState(() => randomKey("pos-shift-close-cancel"));
  const [commitRequestKey, setCommitRequestKey] = useState(() => randomKey("pos-shift-close-commit"));
  const [confirmed, setConfirmed] = useState<ConfirmedCount | null>(null);
  const [closed, setClosed] = useState<ShiftCloseCommitResult["shift"] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const subscription = liveQuery(async () => ({
      terminal: await readTerminalCredential(),
      session: await readLocalEmployeeSession(),
      shift: await readLocalShift(),
    })).subscribe({
      next: setSnapshot,
      error: () => setSnapshot({ terminal: null, session: null, shift: null }),
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const updateMount = () => setMountNode(document.querySelector(".sale-header__actions"));
    updateMount();
    const observer = new MutationObserver(updateMount);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const updateNetwork = () => setOnline(navigator.onLine);
    window.addEventListener("online", updateNetwork);
    window.addEventListener("offline", updateNetwork);
    return () => {
      window.removeEventListener("online", updateNetwork);
      window.removeEventListener("offline", updateNetwork);
    };
  }, []);

  const terminal = snapshot.terminal;
  const session = snapshot.session;
  const actor = session?.actor ?? null;
  const identity = terminal?.identity ?? null;
  const activeShift = snapshot.shift?.shift ?? null;
  const sameScope = Boolean(
    actor &&
      identity &&
      activeShift &&
      actor.runtime_session_status === "ACTIVE" &&
      actor.organization_id === identity.organization_id &&
      actor.branch_id === identity.branch_id &&
      actor.node_id === identity.node_id &&
      activeShift.organization_id === identity.organization_id &&
      activeShift.branch_id === identity.branch_id &&
      activeShift.node_id === identity.node_id,
  );
  const canCloseShift = Boolean(
    sameScope &&
      actor?.permission_codes.includes("pos.shift.close") &&
      activeShift &&
      (activeShift.shift_status === "OPEN" || activeShift.shift_status === "CLOSING"),
  );
  const canLaunch = Boolean(canCloseShift && mountNode && terminal && session && actor && identity && activeShift);

  const gatewayBaseUrl = getGatewayBaseUrl();
  const gateway = useMemo(
    () => (gatewayBaseUrl ? new PosShiftCloseGatewayClient({ baseUrl: gatewayBaseUrl }) : null),
    [gatewayBaseUrl],
  );

  useEffect(() => {
    if (!open) return;

    setRuntime(null);
    setPhase("loading");
    setRoundNo(1);
    setDraft({});
    setAttemptKey(randomKey("pos-shift-count-attempt"));
    setRoundRequestKey(randomKey("pos-shift-count-round"));
    setBeginRequestKey(randomKey("pos-shift-close-begin"));
    setCancelRequestKey(randomKey("pos-shift-close-cancel"));
    setCommitRequestKey(randomKey("pos-shift-close-commit"));
    setConfirmed(null);
    setClosed(null);
    setError(null);
    setNotice(null);
  }, [open]);

  useEffect(() => {
    let active = true;
    if (!open) return () => { active = false; };
    if (!identity) {
      setPhase("start");
      return () => { active = false; };
    }

    void (async () => {
      const cached = await readShiftCloseRuntime(identity);
      if (!active) return;
      if (cached) {
        setRuntime(cached);
        setDraft(emptyDraft(cached.denominations));
        if (cached.shift?.shift_status === "CLOSING") {
          setPhase("count");
          setRoundNo(1);
          setNotice("ใช้ข้อมูลปิดกะล่าสุดในเครื่องระหว่างรอ Core อัปเดต");
        } else {
          setPhase("start");
        }
      } else {
        setPhase("start");
      }

      if (!terminal?.deviceSecret || !session?.sessionToken || !gateway || !online) return;
      setBusy(true);
      const result = await gateway.runtime(terminal.deviceSecret, session.sessionToken);
      if (!active) return;
      setBusy(false);
      if (!result.ok || !result.data) {
        if (!cached) setError(errorText(result.error ?? "POS_SHIFT_CLOSE_RUNTIME_LOAD_FAILED"));
        return;
      }

      await saveShiftCloseRuntime(identity, result.data);
      setRuntime(result.data);
      setDraft(emptyDraft(result.data.denominations));
      if (result.data.shift?.shift_status === "CLOSING") {
        setPhase("count");
        setRoundNo(1);
        setNotice("กะนี้อยู่ระหว่างปิดกะ ระบบเริ่มคู่การนับใหม่แบบ Blind โดยไม่เปิดเผยยอดเดิม");
      } else {
        setPhase("start");
        setNotice(null);
      }
    })();

    return () => { active = false; };
  }, [gateway, identity, online, open, session?.sessionToken, terminal?.deviceSecret]);

  const currencyCode = runtime?.currency.currency_code ?? "THB";
  const denominations = runtime?.denominations ?? [];

  const preview = useMemo(() => {
    let retained = 0;
    let pending = 0;
    for (const denomination of denominations) {
      const row = draft[denomination.denomination_code];
      const retainedQty = Number(row?.retained || 0);
      const pendingQty = Number(row?.pending || 0);
      if (Number.isFinite(retainedQty)) retained += denomination.denomination_value * retainedQty;
      if (Number.isFinite(pendingQty)) pending += denomination.denomination_value * pendingQty;
    }
    return { retained, pending, actual: retained + pending };
  }, [denominations, draft]);

  function buildLines(): ShiftCloseCountLine[] | null {
    const lines: ShiftCloseCountLine[] = [];
    for (const denomination of denominations) {
      const row = draft[denomination.denomination_code] ?? { retained: "", pending: "" };
      const retained = row.retained.trim() === "" ? 0 : Number(row.retained);
      const pending = row.pending.trim() === "" ? 0 : Number(row.pending);
      if (!Number.isSafeInteger(retained) || retained < 0 || !Number.isSafeInteger(pending) || pending < 0) return null;
      lines.push({ denomination_code: denomination.denomination_code, retained_quantity: retained, pending_deposit_quantity: pending });
    }
    return lines;
  }

  function updateDraft(code: string, side: "retained" | "pending", value: string) {
    if (value && !/^\d+$/.test(value)) return;
    setDraft((current) => ({
      ...current,
      [code]: { retained: current[code]?.retained ?? "", pending: current[code]?.pending ?? "", [side]: value },
    }));
  }

  function resetBlindPair(message?: string) {
    setAttemptKey(randomKey("pos-shift-count-attempt"));
    setRoundRequestKey(randomKey("pos-shift-count-round"));
    setRoundNo(1);
    setDraft(emptyDraft(denominations));
    setConfirmed(null);
    setPhase("count");
    setNotice(message ?? null);
  }

  async function beginClose() {
    if (!identity || !terminal || !session || !activeShift || !gateway || !online) {
      setError("การเริ่มปิดกะต้อง Online และมี Employee Session ที่ใช้งานได้");
      return;
    }
    if (!runtime?.shift?.is_responsible_actor) {
      setError("ผู้ปิดกะต้องเป็นผู้รับผิดชอบกะนี้");
      return;
    }

    const parkedCartCount = await countLocalParkedCarts(identity);
    if (parkedCartCount > 0) {
      setNotice(null);
      setError(`ยังมีบิลพัก ${parkedCartCount} บิล กรุณากลับหน้าขายเพื่อขายต่อหรือทิ้งบิลพักให้เรียบร้อยก่อนเริ่มปิดกะ`);
      return;
    }

    setBusy(true);
    setError(null);
    setNotice("กำลังตรวจรายการเงินสดที่รอ Sync ก่อน Freeze กะ…");
    try {
      await syncPendingCashSales(identity, terminal.deviceSecret, session.sessionToken);
      const pendingCashImpact = await countPendingCashImpactOutbox(identity);
      if (pendingCashImpact > 0) {
        setBusy(false);
        setNotice(null);
        setError(`ยังมีรายการเงินสด ${pendingCashImpact} รายการรอ Sync จึงยังเริ่มปิดกะไม่ได้`);
        return;
      }
    } catch (syncError) {
      setBusy(false);
      setNotice(null);
      setError(errorText(syncError instanceof Error ? syncError.message : "CASH_SALE_SYNC_FAILED"));
      return;
    }

    const result = await gateway.begin({
      deviceSecret: terminal.deviceSecret,
      sessionToken: session.sessionToken,
      requestKey: beginRequestKey,
      clientContext: { runtime: "WEBPOS", origin: window.location.origin, terminal_node_id: identity.node_id, actor_employee_id: actor?.employee_id ?? null },
    });
    setBusy(false);
    if (!result.ok || !result.data) {
      setNotice(null);
      setError(errorText(result.error ?? "POS_SHIFT_CLOSE_BEGIN_FAILED"));
      return;
    }

    await saveCoreShift({ ...activeShift, shift_status: "CLOSING" });
    const nextRuntime = runtime?.shift ? { ...runtime, shift: { ...runtime.shift, shift_status: "CLOSING" as const } } : runtime;
    if (nextRuntime) await saveShiftCloseRuntime(identity, nextRuntime);
    setRuntime(nextRuntime);
    resetBlindPair("เริ่มปิดกะแล้ว กรุณานับเงินจริงรอบที่ 1 โดยไม่อ้างอิงยอดจากระบบ");
  }

  async function cancelClose() {
    if (!identity || !terminal || !session || !activeShift || !gateway || !online) {
      setError("การยกเลิกปิดกะต้อง Online");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await gateway.cancel({ deviceSecret: terminal.deviceSecret, sessionToken: session.sessionToken, requestKey: cancelRequestKey, reason: "CANCELLED_FROM_WEBPOS_BEFORE_FINAL_CLOSE" });
    setBusy(false);
    if (!result.ok || !result.data) { setError(errorText(result.error ?? "POS_SHIFT_CLOSE_CANCEL_FAILED")); return; }
    await saveCoreShift({ ...activeShift, shift_status: "OPEN" });
    if (runtime?.shift) await saveShiftCloseRuntime(identity, { ...runtime, shift: { ...runtime.shift, shift_status: "OPEN" } });
    setOpen(false);
    window.location.reload();
  }

  async function submitRound() {
    if (!identity || !terminal || !session || !gateway || !online || !runtime) { setError("การยืนยันการนับเงินต้อง Online"); return; }
    const lines = buildLines();
    if (!lines) { setError("จำนวนธนบัตร/เหรียญต้องเป็นจำนวนเต็มตั้งแต่ 0 ขึ้นไป"); return; }
    setBusy(true);
    setError(null);
    const result = await gateway.count({
      deviceSecret: terminal.deviceSecret, sessionToken: session.sessionToken, attemptKey, roundNo, lines, requestKey: roundRequestKey,
      clientContext: { runtime: "WEBPOS", origin: window.location.origin, terminal_node_id: identity.node_id, actor_employee_id: actor?.employee_id ?? null },
    });
    setBusy(false);
    if (!result.ok || !result.data) { setError(errorText(result.error ?? "POS_SHIFT_CLOSE_COUNT_FAILED")); return; }
    if (roundNo === 1) {
      setRoundNo(2);
      setRoundRequestKey(randomKey("pos-shift-count-round"));
      setDraft(emptyDraft(denominations));
      setNotice("บันทึกรอบที่ 1 แล้ว · รอบที่ 2 เริ่มจากช่องว่างใหม่ทั้งหมด");
      return;
    }
    if (result.data.recount_required || !result.data.confirmed) { resetBlindPair("ยอดนับสองรอบไม่ตรง กรุณานับใหม่"); return; }
    const confirmationEventId = result.data.confirmation_event_id ?? "";
    if (!confirmationEventId) { setError("Server ยืนยันการนับแล้วแต่ไม่คืน Confirmation ID กรุณานับใหม่"); return; }
    setConfirmed({ confirmationEventId, actualCash: Number(result.data.actual_cash ?? 0), retainedFloat: Number(result.data.retained_float ?? 0), pendingDeposit: Number(result.data.pending_deposit ?? 0) });
    setNotice(null);
    setPhase("review");
  }

  async function commitClose() {
    if (!identity || !terminal || !session || !gateway || !online || !confirmed) { setError("การปิดกะขั้นสุดท้ายต้อง Online และมีผลนับที่ยืนยันแล้ว"); return; }
    setBusy(true);
    setError(null);
    const result = await gateway.commit({
      deviceSecret: terminal.deviceSecret, sessionToken: session.sessionToken, confirmationEventId: confirmed.confirmationEventId, requestKey: commitRequestKey,
      clientContext: { runtime: "WEBPOS", origin: window.location.origin, terminal_node_id: identity.node_id, actor_employee_id: actor?.employee_id ?? null },
    });
    setBusy(false);
    if (!result.ok || !result.data) { setError(errorText(result.error ?? "POS_SHIFT_CLOSE_COMMIT_FAILED")); return; }
    setClosed(result.data.shift);
    await clearLocalShift();
    setPhase("success");
  }

  function leaveAfterClose() { setOpen(false); window.location.reload(); }

  if (!mountNode) return null;

  const launch = canLaunch ? createPortal(
    <button className="sale-header__button shift-close-launch" type="button" onClick={() => setOpen(true)}>
      {activeShift?.shift_status === "CLOSING" ? "ทำต่อปิดกะ" : "ปิดกะ"}
    </button>,
    mountNode,
  ) : null;

  if (!open) return launch;

  const responsible = runtime?.shift?.is_responsible_actor ?? activeShift?.responsible_employee_id === actor?.employee_id;
  const canDismiss = phase === "start" && runtime?.shift?.shift_status !== "CLOSING";

  const modal = createPortal(
    <div className="pos-modal-backdrop shift-close-backdrop" role="presentation">
      <section className="pos-modal shift-close-modal" role="dialog" aria-modal="true" aria-labelledby="shift-close-title">
        <div className="pos-modal__header shift-close-modal__header">
          <div><p className="eyebrow">BLIND CASH COUNT</p><h2 id="shift-close-title">ปิดกะ</h2><p>{actor?.employee_name ?? "พนักงาน"} · {identity?.branch_name ?? "POS"}</p></div>
          {canDismiss ? <button type="button" className="pos-modal__close" disabled={busy} onClick={() => setOpen(false)} aria-label="ปิดหน้าต่างปิดกะ">×</button> : null}
        </div>

        {!online ? <div className="shift-close-alert">Offline: อ่านชนิดเงินและสถานะล่าสุดจากเครื่องได้ แต่การเริ่มนับ/ยืนยัน/ปิดกะต้อง Online</div> : null}
        {error ? <div className="shift-close-alert shift-close-alert--error">{error}</div> : null}
        {notice ? <div className="shift-close-alert shift-close-alert--info">{notice}</div> : null}

        {phase === "loading" ? (
          <div className="shift-close-loading">กำลังอ่านสถานะกะจาก Local Runtime…</div>
        ) : phase === "start" ? (
          <div className="shift-close-start">
            <div className="shift-close-rule-card"><strong>กติกาการปิดกะ</strong><p>ระบบจะไม่แสดงเงินที่ควรมีในลิ้นชักก่อนนับ คุณต้องนับเงินจริง 2 รอบ โดยรอบที่ 2 จะไม่เห็นข้อมูลจากรอบที่ 1</p></div>
            <dl className="shift-close-meta">
              <div><dt>ผู้รับผิดชอบกะ</dt><dd>{activeShift?.responsible_employee_name ?? "—"}</dd></div>
              <div><dt>เปิดกะเมื่อ</dt><dd>{formatDateTime(activeShift?.opened_at ?? null)}</dd></div>
              <div><dt>สถานะ</dt><dd>{runtime?.shift?.shift_status ?? activeShift?.shift_status ?? "—"}</dd></div>
            </dl>
            {!responsible ? <div className="locked-note">กะนี้ต้องปิดโดยผู้รับผิดชอบกะเดิม การเปลี่ยนผู้รับผิดชอบต้องใช้ Handover Contract แยก</div> : (
              <button className="primary-action shift-close-primary" type="button" disabled={busy || !online || !runtime?.shift} onClick={() => void beginClose()}>{busy ? "กำลังเตรียมปิดกะ…" : "เริ่มปิดกะและนับเงินจริง"}</button>
            )}
          </div>
        ) : phase === "count" ? (
          <div className="shift-close-count">
            <div className="shift-close-round-header"><div><span>รอบนับ</span><strong>{roundNo} / 2</strong></div><p>{roundNo === 1 ? "นับเงินจริงตามสภาพที่อยู่ตรงหน้า" : "นับใหม่อีกครั้งจากศูนย์ โดยไม่อ้างอิงรอบแรก"}</p></div>
            <div className="shift-close-table-wrap">
              <table className="shift-close-table">
                <thead><tr><th>ชนิดเงิน</th><th>เงินทอนกะถัดไป</th><th>เงินเก็บรอนำฝาก</th><th>รวมแถวนี้</th></tr></thead>
                <tbody>
                  {denominations.map((denomination) => {
                    const row = draft[denomination.denomination_code] ?? { retained: "", pending: "" };
                    const retainedQty = Number(row.retained || 0);
                    const pendingQty = Number(row.pending || 0);
                    const rowTotal = denomination.denomination_value * ((Number.isFinite(retainedQty) ? retainedQty : 0) + (Number.isFinite(pendingQty) ? pendingQty : 0));
                    return (
                      <tr key={denomination.denomination_code}>
                        <th scope="row"><strong>{denomination.display_label}</strong><small>{denomination.denomination_kind === "NOTE" ? "ธนบัตร" : "เหรียญ"}</small></th>
                        <td><input aria-label={`${denomination.display_label} เงินทอนกะถัดไป`} inputMode="numeric" pattern="[0-9]*" value={row.retained} onChange={(event) => updateDraft(denomination.denomination_code, "retained", event.target.value)} placeholder="0" /></td>
                        <td><input aria-label={`${denomination.display_label} เงินเก็บรอนำฝาก`} inputMode="numeric" pattern="[0-9]*" value={row.pending} onChange={(event) => updateDraft(denomination.denomination_code, "pending", event.target.value)} placeholder="0" /></td>
                        <td><strong>{formatMoney(rowTotal, currencyCode)}</strong></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr><th>รวม</th><td><strong>{formatMoney(preview.retained, currencyCode)}</strong></td><td><strong>{formatMoney(preview.pending, currencyCode)}</strong></td><td><strong>{formatMoney(preview.actual, currencyCode)}</strong></td></tr></tfoot>
              </table>
            </div>
            <div className="shift-close-actions">
              <button className="primary-action shift-close-primary" type="button" disabled={busy || !online} onClick={() => void submitRound()}>{busy ? "กำลังยืนยัน…" : roundNo === 1 ? "ยืนยันรอบที่ 1" : "ยืนยันรอบที่ 2"}</button>
              <button className="secondary-action" type="button" disabled={busy || !online} onClick={() => void cancelClose()}>ยกเลิกการปิดกะ</button>
            </div>
            <p className="form-note">จำนวนเงินต่อแถวเป็น Preview จากจำนวนที่คุณกรอกเท่านั้น Server จะ Resolve มูลค่า Denomination และยืนยันอีกครั้ง</p>
          </div>
        ) : phase === "review" && confirmed ? (
          <div className="shift-close-review">
            <div className="shift-close-review__hero"><p className="eyebrow">CUSTOM REVIEW</p><h3>ตรวจทานเงินจริงก่อนปิดกะ</h3><p>ระบบยังไม่แสดง Expected Cash หรือผลต่างให้พนักงานหน้าร้าน</p></div>
            <div className="shift-close-summary-grid">
              <div><span>เงินจริงที่นับได้</span><strong>{formatMoney(confirmed.actualCash, currencyCode)}</strong></div>
              <div><span>เงินทอนกะถัดไป</span><strong>{formatMoney(confirmed.retainedFloat, currencyCode)}</strong></div>
              <div><span>เงินเก็บรอนำฝาก</span><strong>{formatMoney(confirmed.pendingDeposit, currencyCode)}</strong></div>
            </div>
            <div className="shift-close-actions"><button className="primary-action shift-close-primary" type="button" disabled={busy || !online} onClick={() => void commitClose()}>{busy ? "กำลังปิดกะ…" : "ยืนยันปิดกะ"}</button><button className="secondary-action" type="button" disabled={busy || !online} onClick={() => void cancelClose()}>ยกเลิกการปิดกะ</button></div>
          </div>
        ) : phase === "success" && closed ? (
          <div className="shift-close-success">
            <div className="shift-close-success__mark">✓</div><p className="eyebrow">SHIFT CLOSED</p><h3>บันทึกการปิดกะเรียบร้อย</h3><p>ปิดเมื่อ {formatDateTime(closed.closed_at)}</p>
            <div className="shift-close-summary-grid">
              <div><span>เงินจริงที่นับได้</span><strong>{formatMoney(closed.actual_cash, closed.currency_code)}</strong></div>
              <div><span>เงินทอนกะถัดไป</span><strong>{formatMoney(closed.retained_float, closed.currency_code)}</strong></div>
              <div><span>เงินเก็บรอนำฝาก</span><strong>{formatMoney(closed.pending_deposit, closed.currency_code)}</strong></div>
            </div>
            <p className="form-note">ผล Expected / Difference / Reconciliation ถูกเก็บใน Core สำหรับการตรวจสอบตาม Permission และไม่เปิดเผยบน POS Frontline</p>
            <button className="primary-action shift-close-primary" type="button" onClick={leaveAfterClose}>กลับหน้าขาย</button>
          </div>
        ) : null}
      </section>
    </div>,
    document.body,
  );

  return <>{launch}{modal}</>;
}
